import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Divider,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Paper,
  IconButton,
  Tooltip,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import {
  ArrowBack,
  Save,
  CalendarToday,
  Person,
  Business,
  Phone,
  Email,
  LocationOn,
  EmojiEvents,
  AttachMoney,
  Description,
  Schedule,
  CheckCircle,
  Warning,
  Info,
  ExpandMore,
  Payment,
  CreditCard,
  PhoneAndroid,
  AccountBalance
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getCustomers } from '../redux/actions/customerActions';
import { getGoldsmiths } from '../redux/actions/goldsmithActions';
import { createCustomerOrder } from '../redux/actions/goldsmithActions';
import { getActivePaymentModes } from '../redux/actions/paymentModeActions';

const NewCustomerOrder = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const { customers, loading: customersLoading } = useSelector(state => state.customers);
  const { goldsmiths, goldsmithsLoading } = useSelector(state => state.goldsmith);
  const { ordersLoading } = useSelector(state => state.goldsmith);
  const { activePaymentModes } = useSelector(state => state.paymentModes);

  const [expandedAccordion, setExpandedAccordion] = useState(0);
  const [formData, setFormData] = useState({
    customer_id: '',
    goldsmith_id: '',
    product_name: '',
    description: '',
    estimated_weight: '',
    estimated_touch: '',
    order_date: new Date().toISOString().split('T')[0], // Today's date
    expected_delivery_date: '',
    advance_payment: '',
    payment_mode: 'cash',
    notes: ''
  });
  const [paymentModes, setPaymentModes] = useState([]); // Array of payment mode objects
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);

  // Payment mode icons mapping
  const paymentIcons = {
    'AttachMoney': <AttachMoney />,
    'CreditCard': <CreditCard />,
    'PhoneAndroid': <PhoneAndroid />,
    'AccountBalance': <AccountBalance />,
    'Payment': <Payment />
  };

  // Style to prevent strikethrough on labels
  const labelStyle = {
    '&.Mui-disabled': {
      color: 'text.primary',
      textDecoration: 'none'
    }
  };

  useEffect(() => {
    dispatch(getCustomers());
    dispatch(getGoldsmiths());
    dispatch(getActivePaymentModes());
  }, [dispatch]);

  const accordionSections = [
    { id: 0, title: 'Customer Selection', icon: <Person /> },
    { id: 1, title: 'Product Details', icon: <EmojiEvents /> },
    { id: 2, title: 'Goldsmith Assignment', icon: <Business /> },
    { id: 3, title: 'Payment & Delivery', icon: <AttachMoney /> },
    { id: 4, title: 'Review & Confirm', icon: <CheckCircle /> }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateAccordion = (accordionIndex) => {
    const newErrors = {};
    
    switch (accordionIndex) {
      case 0: // Customer Selection
        if (!formData.customer_id) {
          newErrors.customer_id = 'Please select a customer';
        }
        break;
      case 1: // Product Details
        if (!formData.product_name.trim()) {
          newErrors.product_name = 'Product name is required';
        }
        if (!formData.estimated_weight) {
          newErrors.estimated_weight = 'Estimated weight is required';
        }
        if (!formData.estimated_touch) {
          newErrors.estimated_touch = 'Estimated touch is required';
        }
        break;
      case 2: // Goldsmith Assignment
        if (!formData.goldsmith_id) {
          newErrors.goldsmith_id = 'Please select a goldsmith';
        }
        break;
      case 3: // Payment & Delivery
        if (!formData.expected_delivery_date) {
          newErrors.expected_delivery_date = 'Expected delivery date is required';
        }
        if (formData.advance_payment && isNaN(formData.advance_payment)) {
          newErrors.advance_payment = 'Advance payment must be a valid number';
        }
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedAccordion(isExpanded ? panel : false);
  };

  const getPaymentIcon = (iconName) => {
    return paymentIcons[iconName] || <Payment />;
  };

  const handlePaymentModeToggle = (mode) => {
    setPaymentModes(prev => {
      const existing = prev.find(p => p.modeId === mode.id);
      if (existing) {
        return prev.filter(p => p.modeId !== mode.id);
      } else {
        return [...prev, {
          modeId: mode.id,
          modeName: mode.name,
          modeCode: mode.code,
          amount: '',
          reference: '',
          bankDetails: ''
        }];
      }
    });
  };

  const handlePaymentAmountChange = (modeId, amount) => {
    setPaymentModes(prev => 
      prev.map(p => p.modeId === modeId ? { ...p, amount } : p)
    );
  };

  const handlePaymentReferenceChange = (modeId, reference) => {
    setPaymentModes(prev => 
      prev.map(p => p.modeId === modeId ? { ...p, reference } : p)
    );
  };

  const handlePaymentBankDetailsChange = (modeId, bankDetails) => {
    setPaymentModes(prev => 
      prev.map(p => p.modeId === modeId ? { ...p, bankDetails } : p)
    );
  };

  const calculatePaymentTotal = () => {
    return paymentModes.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
  };

  const handleSubmit = async () => {
    // Validate all accordions
    const isCustomerValid = validateAccordion(0);
    const isProductValid = validateAccordion(1);
    const isGoldsmithValid = validateAccordion(2);
    const isPaymentValid = validateAccordion(3);
    
    if (!isCustomerValid || !isProductValid || !isGoldsmithValid || !isPaymentValid) {
      return;
    }

    try {
      const orderData = {
        customer_id: formData.customer_id,
        goldsmith_id: formData.goldsmith_id,
        order_date: formData.order_date,
        expected_delivery_date: formData.expected_delivery_date,
        product_description: formData.description, // Map description to product_description
        design_details: formData.product_name, // Map product_name to design_details
        estimated_weight: parseFloat(formData.estimated_weight),
        estimated_touch: parseFloat(formData.estimated_touch),
        advance_amount: parseFloat(formData.advance_payment) || 0,
        notes: formData.notes,
        payment_modes: paymentModes.map(p => ({
          mode_code: p.modeCode,
          amount: parseFloat(p.amount),
          reference: p.reference,
          bank_details: p.bankDetails,
        }))
      };

      await dispatch(createCustomerOrder(orderData));
      setSuccess(true);
      
      setTimeout(() => {
        navigate('/customer-orders');
      }, 2000);
    } catch (error) {
      console.error('Error creating order:', error);
    }
  };

  const selectedCustomer = customers.find(c => c.id === formData.customer_id);
  const selectedGoldsmith = goldsmiths.find(g => g.id === formData.goldsmith_id);

  const calculateEstimatedPure = () => {
    if (formData.estimated_weight && formData.estimated_touch) {
      return (parseFloat(formData.estimated_weight) * parseFloat(formData.estimated_touch) / 100).toFixed(3);
    }
    return '0.000';
  };

  if (success) {
    return (
      <Box sx={{ p: 3 }}>
        <Card sx={{ maxWidth: 600, mx: 'auto', textAlign: 'center' }}>
          <CardContent sx={{ p: 4 }}>
            <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h4" gutterBottom color="success.main">
              Order Created Successfully!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Your customer order has been created and assigned to the goldsmith.
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/customer-orders')}
              startIcon={<ArrowBack />}
            >
              View All Orders
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={() => navigate('/customer-orders')}>
          <ArrowBack />
        </IconButton>
        <Box>
          <Typography variant="h4" gutterBottom>
            Create New Customer Order
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Create a new customer order and assign it to a goldsmith
          </Typography>
        </Box>
      </Box>

      {/* Accordion Form */}
      <Card>
        <CardContent sx={{ p: 4 }}>
          {/* Accordion 1: Customer Selection */}
          <Accordion 
            expanded={expandedAccordion === 0} 
            onChange={handleAccordionChange(0)}
            sx={{ mb: 2 }}
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{ 
                bgcolor: expandedAccordion === 0 ? 'primary.50' : 'grey.50',
                '&:hover': { bgcolor: 'primary.100' }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Person color="primary" />
                <Typography variant="h6" color="primary">
                  Customer Selection
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              
              <FormControl fullWidth error={!!errors.customer_id} sx={{ mb: 3 }}>
                <InputLabel 
                  shrink={true}
                  sx={labelStyle}
                >
                  Select Customer
                </InputLabel>
                <Select
                  value={formData.customer_id}
                  onChange={(e) => handleInputChange('customer_id', e.target.value)}
                  disabled={customersLoading}
                  label="Select Customer"
                >
                  {customers.map((customer) => (
                    <MenuItem key={customer.id} value={customer.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <Person />
                        <Box>
                          <Typography variant="body1">{customer.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {customer.phone} • {customer.email}
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {errors.customer_id && (
                  <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                    {errors.customer_id}
                  </Typography>
                )}
              </FormControl>

              {selectedCustomer && (
                <Paper sx={{ p: 2, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200' }}>
                  <Typography variant="subtitle2" gutterBottom color="primary">
                    Selected Customer Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2"><strong>Name:</strong> {selectedCustomer.name}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2"><strong>Phone:</strong> {selectedCustomer.phone}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2"><strong>Email:</strong> {selectedCustomer.email}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2"><strong>Address:</strong> {selectedCustomer.address}</Typography>
                    </Grid>
                  </Grid>
                </Paper>
              )}
            </AccordionDetails>
          </Accordion>

          {/* Accordion 2: Product Details */}
          <Accordion 
            expanded={expandedAccordion === 1} 
            onChange={handleAccordionChange(1)}
            sx={{ mb: 2 }}
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{ 
                bgcolor: expandedAccordion === 1 ? 'success.50' : 'grey.50',
                '&:hover': { bgcolor: 'success.100' }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EmojiEvents color="success" />
                <Typography variant="h6" color="success.main">
                  Product Details
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Product Name"
                    value={formData.product_name}
                    onChange={(e) => handleInputChange('product_name', e.target.value)}
                    error={!!errors.product_name}
                    helperText={errors.product_name}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><EmojiEvents /></InputAdornment>
                    }}
                    InputLabelProps={{
                      sx: labelStyle
                    }}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Product Description"
                    multiline
                    rows={3}
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><Description /></InputAdornment>
                    }}
                    InputLabelProps={{
                      sx: labelStyle
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Estimated Weight (grams)"
                    type="number"
                    value={formData.estimated_weight}
                    onChange={(e) => handleInputChange('estimated_weight', e.target.value)}
                    error={!!errors.estimated_weight}
                    helperText={errors.estimated_weight}
                    InputProps={{
                      inputProps: { min: 0, step: 0.001 }
                    }}
                    InputLabelProps={{
                      sx: labelStyle
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Estimated Touch (%)"
                    type="number"
                    value={formData.estimated_touch}
                    onChange={(e) => handleInputChange('estimated_touch', e.target.value)}
                    error={!!errors.estimated_touch}
                    helperText={errors.estimated_touch}
                    InputProps={{
                      inputProps: { min: 0, max: 100, step: 0.1 }
                    }}
                    InputLabelProps={{
                      sx: labelStyle
                    }}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, bgcolor: 'info.50', border: '1px solid', borderColor: 'info.200' }}>
                    <Typography variant="subtitle2" gutterBottom color="info.main">
                      Total Pure
                    </Typography>
                    <Typography variant="h6" color="info.main">
                      {calculateEstimatedPure()} grams
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Accordion 3: Goldsmith Assignment */}
          <Accordion 
            expanded={expandedAccordion === 2} 
            onChange={handleAccordionChange(2)}
            sx={{ mb: 2 }}
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{ 
                bgcolor: expandedAccordion === 2 ? 'warning.50' : 'grey.50',
                '&:hover': { bgcolor: 'warning.100' }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Business color="warning" />
                <Typography variant="h6" color="warning.main">
                  Goldsmith Assignment
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              
              <FormControl fullWidth error={!!errors.goldsmith_id} sx={{ mb: 3 }}>
                <InputLabel 
                  shrink={true}
                  sx={labelStyle}
                >
                  Select Goldsmith
                </InputLabel>
                <Select
                  value={formData.goldsmith_id}
                  onChange={(e) => handleInputChange('goldsmith_id', e.target.value)}
                  disabled={goldsmithsLoading}
                  label="Select Goldsmith"
                >
                  {goldsmiths.map((goldsmith) => (
                    <MenuItem key={goldsmith.id} value={goldsmith.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <Business />
                        <Box>
                          <Typography variant="body1">{goldsmith.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {goldsmith.contact_person} • {goldsmith.phone}
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {errors.goldsmith_id && (
                  <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                    {errors.goldsmith_id}
                  </Typography>
                )}
              </FormControl>

              {selectedGoldsmith && (
                <Paper sx={{ p: 2, bgcolor: 'secondary.50', border: '1px solid', borderColor: 'secondary.200' }}>
                  <Typography variant="subtitle2" gutterBottom color="secondary.main">
                    Selected Goldsmith Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2"><strong>Name:</strong> {selectedGoldsmith.name}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2"><strong>Contact:</strong> {selectedGoldsmith.contact_person}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2"><strong>Phone:</strong> {selectedGoldsmith.phone}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2"><strong>Email:</strong> {selectedGoldsmith.email}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2"><strong>Address:</strong> {selectedGoldsmith.address}</Typography>
                    </Grid>
                  </Grid>
                </Paper>
              )}
            </AccordionDetails>
          </Accordion>

          {/* Accordion 4: Payment & Delivery */}
          <Accordion 
            expanded={expandedAccordion === 3} 
            onChange={handleAccordionChange(3)}
            sx={{ mb: 2 }}
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{ 
                bgcolor: expandedAccordion === 3 ? 'info.50' : 'grey.50',
                '&:hover': { bgcolor: 'info.100' }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AttachMoney color="info" />
                <Typography variant="h6" color="info.main">
                  Payment & Delivery
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Order Date"
                    type="date"
                    value={formData.order_date}
                    onChange={(e) => handleInputChange('order_date', e.target.value)}
                    error={!!errors.order_date}
                    helperText={errors.order_date}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><CalendarToday /></InputAdornment>
                    }}
                    InputLabelProps={{ 
                      shrink: true,
                      sx: labelStyle
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Expected Delivery Date"
                    type="date"
                    value={formData.expected_delivery_date}
                    onChange={(e) => handleInputChange('expected_delivery_date', e.target.value)}
                    error={!!errors.expected_delivery_date}
                    helperText={errors.expected_delivery_date}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><Schedule /></InputAdornment>
                    }}
                    InputLabelProps={{ 
                      shrink: true,
                      sx: labelStyle
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Advance Payment (₹)"
                    type="number"
                    value={formData.advance_payment}
                    onChange={(e) => handleInputChange('advance_payment', e.target.value)}
                    error={!!errors.advance_payment}
                    helperText={errors.advance_payment}
                    InputProps={{
                      inputProps: { min: 0, step: 0.01 },
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>
                    }}
                    InputLabelProps={{
                      sx: labelStyle
                    }}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Payment color="info" />
                    Payment Modes
                  </Typography>
                  
                  {/* Payment Modes Accordion */}
                  <Box sx={{ mb: 2 }}>
                    {activePaymentModes.map((mode) => {
                      const isSelected = paymentModes.some(p => p.modeId === mode.id);
                      const selectedPayment = paymentModes.find(p => p.modeId === mode.id);
                      
                      return (
                        <Accordion 
                          key={mode.id} 
                          expanded={isSelected}
                          onChange={() => handlePaymentModeToggle(mode)}
                          sx={{ mb: 1 }}
                        >
                          <AccordionSummary
                            expandIcon={<ExpandMore />}
                            sx={{ 
                              bgcolor: isSelected ? (mode.color || 'primary.main') + '20' : 'grey.50',
                              '&:hover': { bgcolor: (mode.color || 'primary.main') + '10' }
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                              <Checkbox
                                checked={isSelected}
                                onChange={() => handlePaymentModeToggle(mode)}
                                onClick={(e) => e.stopPropagation()}
                                color="primary"
                              />
                              <Box sx={{ color: mode.color || 'primary.main', display: 'flex', alignItems: 'center' }}>
                                {getPaymentIcon(mode.icon)}
                              </Box>
                              <Typography variant="body1" fontWeight={isSelected ? 'bold' : 'normal'}>
                                {mode.name}
                              </Typography>
                              {isSelected && selectedPayment?.amount && (
                                <Chip 
                                  label={`₹${selectedPayment.amount}`} 
                                  size="small" 
                                  color="success" 
                                  sx={{ ml: 'auto' }}
                                />
                              )}
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Grid container spacing={2}>
                              <Grid item xs={12}>
                                <TextField
                                  fullWidth
                                  label="Amount (₹)"
                                  type="number"
                                  value={selectedPayment?.amount || ''}
                                  onChange={(e) => handlePaymentAmountChange(mode.id, e.target.value)}
                                  placeholder="Enter amount"
                                  InputProps={{
                                    inputProps: { step: "0.01", min: 0 },
                                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                  }}
                                  InputLabelProps={{
                                    sx: labelStyle
                                  }}
                                />
                              </Grid>
                              
                              {mode.requires_reference && (
                                <Grid item xs={12}>
                                  <TextField
                                    fullWidth
                                    label="Transaction Reference"
                                    value={selectedPayment?.reference || ''}
                                    onChange={(e) => handlePaymentReferenceChange(mode.id, e.target.value)}
                                    placeholder="Enter transaction reference number"
                                    InputLabelProps={{
                                      sx: labelStyle
                                    }}
                                  />
                                </Grid>
                              )}
                              
                              {mode.requires_bank && (
                                <Grid item xs={12}>
                                  <TextField
                                    fullWidth
                                    label="Bank Details"
                                    value={selectedPayment?.bankDetails || ''}
                                    onChange={(e) => handlePaymentBankDetailsChange(mode.id, e.target.value)}
                                    multiline
                                    rows={2}
                                    placeholder="Enter bank name, account number, etc."
                                    InputLabelProps={{
                                      sx: labelStyle
                                    }}
                                  />
                                </Grid>
                              )}
                            </Grid>
                          </AccordionDetails>
                        </Accordion>
                      );
                    })}
                  </Box>

                  {/* Payment Summary */}
                  {paymentModes.length > 0 && (
                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Payment Summary
                      </Typography>
                      {paymentModes.map((payment) => (
                        <Box key={payment.modeId} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="body2">{payment.modeName}:</Typography>
                          <Typography variant="body2" fontWeight="bold">₹{Math.round(parseFloat(payment.amount || 0))}</Typography>
                        </Box>
                      ))}
                      <Divider sx={{ my: 1 }} />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body1" fontWeight="bold">Total Payment:</Typography>
                        <Typography variant="body1" fontWeight="bold">₹{Math.round(calculatePaymentTotal())}</Typography>
                      </Box>
                    </Box>
                  )}
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Additional Notes"
                    multiline
                    rows={3}
                    value={formData.notes}
                    onChange={(e) => handleInputChange('notes', e.target.value)}
                    placeholder="Any special instructions or notes for the goldsmith..."
                    InputLabelProps={{
                      sx: labelStyle
                    }}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Accordion 5: Review & Confirm */}
          <Accordion 
            expanded={expandedAccordion === 4} 
            onChange={handleAccordionChange(4)}
            sx={{ mb: 2 }}
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{ 
                bgcolor: expandedAccordion === 4 ? 'success.50' : 'grey.50',
                '&:hover': { bgcolor: 'success.100' }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircle color="success" />
                <Typography variant="h6" color="success.main">
                  Review & Confirm
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="subtitle1" gutterBottom color="primary">
                      Customer Information
                    </Typography>
                    {selectedCustomer && (
                      <Box>
                        <Typography variant="body2"><strong>Name:</strong> {selectedCustomer.name}</Typography>
                        <Typography variant="body2"><strong>Phone:</strong> {selectedCustomer.phone}</Typography>
                        <Typography variant="body2"><strong>Email:</strong> {selectedCustomer.email}</Typography>
                      </Box>
                    )}
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="subtitle1" gutterBottom color="secondary">
                      Product Details
                    </Typography>
                    <Typography variant="body2"><strong>Product:</strong> {formData.product_name}</Typography>
                    <Typography variant="body2"><strong>Weight:</strong> {formData.estimated_weight} grams</Typography>
                    <Typography variant="body2"><strong>Touch:</strong> {formData.estimated_touch}%</Typography>
                    <Typography variant="body2"><strong>Pure Gold:</strong> {calculateEstimatedPure()} grams</Typography>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="subtitle1" gutterBottom color="info">
                      Goldsmith Assignment
                    </Typography>
                    {selectedGoldsmith && (
                      <Box>
                        <Typography variant="body2"><strong>Name:</strong> {selectedGoldsmith.name}</Typography>
                        <Typography variant="body2"><strong>Contact:</strong> {selectedGoldsmith.contact_person}</Typography>
                        <Typography variant="body2"><strong>Phone:</strong> {selectedGoldsmith.phone}</Typography>
                      </Box>
                    )}
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="subtitle1" gutterBottom color="success">
                      Payment & Delivery
                    </Typography>
                    <Typography variant="body2"><strong>Order Date:</strong> {formData.order_date}</Typography>
                    <Typography variant="body2"><strong>Expected Delivery:</strong> {formData.expected_delivery_date}</Typography>
                    <Typography variant="body2"><strong>Advance Payment:</strong> ₹{formData.advance_payment}</Typography>
                    
                    {paymentModes.length > 0 ? (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" fontWeight="bold">Payment Modes:</Typography>
                        {paymentModes.map((payment) => (
                          <Typography key={payment.modeId} variant="body2" sx={{ ml: 1 }}>
                            • {payment.modeName}: ₹{payment.amount}
                          </Typography>
                        ))}
                        <Typography variant="body2" fontWeight="bold" sx={{ mt: 1 }}>
                          Total: ₹{Math.round(calculatePaymentTotal())}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">No payment modes selected</Typography>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Submit Button */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={ordersLoading}
              startIcon={ordersLoading ? <CircularProgress size={20} /> : <Save />}
              size="large"
              sx={{
                background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)',
                px: 4,
                py: 1.5,
                fontSize: '1.1rem'
              }}
            >
              {ordersLoading ? 'Creating Order...' : 'Create Order'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default NewCustomerOrder;
